/**
 * AssignedSkillQuizModal Component
 * Presents training-profile quiz assignments inside a professional modal flow
 * with collection selection, setup, in-modal quiz play, and results.
 *
 * @module AssignedSkillQuizModal
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Image,
  Progress,
  Select,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleOutlined,
  CloseOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  HomeOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import RadarChart from '../../../components/RadarChart/RadarChart';
import quizCollectionsService from '../../../services/quizCollectionsService';
import { getFileUrl } from '../../../utils/urlUtils';
import styles from './AssignedSkillQuizModal.module.css';

const { Title, Text } = Typography;

const QUESTION_COUNT_PRESETS = [5, 10, 15, 20, 25, 30];
const RADAR_COLORS = [
  '#1890ff', '#52c41a', '#722ed1', '#faad14', '#13c2c2', '#eb2f96',
  '#fa541c', '#2f54eb', '#a0d911', '#fa8c16', '#9254de', '#f5222d',
];

const sortChoiceIds = (choiceIds) => [...new Set(choiceIds || [])].sort((left, right) => left - right);

const areChoiceSetsEqual = (left, right) => {
  const normalizedLeft = sortChoiceIds(left);
  const normalizedRight = sortChoiceIds(right);

  if (normalizedLeft.length !== normalizedRight.length) {
    return false;
  }

  return normalizedLeft.every((choiceId, index) => choiceId === normalizedRight[index]);
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const buildQuestionCountOptions = (questionCount) => {
  const safeCount = Number(questionCount) || 0;
  if (safeCount <= 0) {
    return [];
  }

  return Array.from(new Set([...QUESTION_COUNT_PRESETS.filter((count) => count <= safeCount), safeCount]))
    .sort((left, right) => left - right)
    .map((count) => ({ value: count, label: `${count}` }));
};

const MindMapSkillIcon = ({ size = 28, color = '#1890ff' }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="16" cy="16" r="6" fill={color} opacity="0.9" />
    <circle cx="16" cy="16" r="4" fill="#fff" opacity="0.28" />
    <circle cx="6" cy="8" r="3.5" fill="#ffd666" />
    <circle cx="26" cy="8" r="3.5" fill="#95de64" />
    <circle cx="6" cy="24" r="3.5" fill="#ff9c6e" />
    <circle cx="26" cy="24" r="3.5" fill="#b37feb" />
    <circle cx="16" cy="4" r="2" fill="#69c0ff" />
    <circle cx="16" cy="28" r="2" fill="#ff85c0" />
    <path d="M11 12L8 10M21 12L24 10M11 20L8 22M21 20L24 22M16 10V6M16 22V26"
      stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
  </svg>
);

const LeftPuzzleDecoration = () => (
  <svg className={styles.puzzleDecorationLeft} width="80" height="400" viewBox="0 0 80 400" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(10, 30)">
      <path d="M0 20 C0 9, 9 0, 20 0 L40 0 C40 0, 42 8, 50 8 C58 8, 60 0, 60 0 L60 40 C60 40, 52 42, 52 50 C52 58, 60 60, 60 60 L60 80 C60 80, 58 72, 50 72 C42 72, 40 80, 40 80 L20 80 C9 80, 0 71, 0 60 L0 20Z" fill="#1890ff" opacity="0.12" />
      <circle cx="30" cy="40" r="12" fill="#1890ff" opacity="0.2" />
      <text x="30" y="44" textAnchor="middle" fill="#1890ff" fontSize="10" fontWeight="600">AI</text>
    </g>
    <g transform="translate(5, 130)">
      <path d="M0 0 L30 0 C30 0, 32 10, 42 10 C52 10, 54 0, 54 0 L54 50 L0 50 L0 0Z" fill="#52c41a" opacity="0.12" />
      <circle cx="27" cy="25" r="10" fill="#52c41a" opacity="0.2" />
      <text x="27" y="29" textAnchor="middle" fill="#52c41a" fontSize="10" fontWeight="600">Q</text>
    </g>
    <g transform="translate(15, 210)">
      <rect x="0" y="0" width="50" height="50" rx="8" fill="#722ed1" opacity="0.12" />
      <path d="M25 10 L25 40 M10 25 L40 25" stroke="#722ed1" strokeWidth="3" strokeLinecap="round" opacity="0.3" />
    </g>
    <path d="M35 90 Q 25 110, 35 130 Q 45 160, 30 180 Q 15 200, 40 230 Q 55 260, 35 290" stroke="#d9d9d9" strokeWidth="2" strokeDasharray="4 4" fill="none" />
    <g transform="translate(10, 300)">
      <circle cx="25" cy="25" r="20" fill="#faad14" opacity="0.1" />
      <circle cx="25" cy="25" r="12" fill="#faad14" opacity="0.15" />
      <text x="25" y="30" textAnchor="middle" fill="#faad14" fontSize="12">GO</text>
    </g>
  </svg>
);

const RightPuzzleDecoration = () => (
  <svg className={styles.puzzleDecorationRight} width="80" height="400" viewBox="0 0 80 400" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(15, 20)">
      <polygon points="25,5 30,20 45,20 33,30 38,45 25,36 12,45 17,30 5,20 20,20" fill="#faad14" opacity="0.15" />
      <circle cx="25" cy="25" r="8" fill="#faad14" opacity="0.25" />
    </g>
    <g transform="translate(10, 90)">
      <path d="M50 0 L50 25 C50 25, 58 27, 58 35 C58 43, 50 45, 50 45 L50 70 L0 70 L0 25 C0 25, 8 23, 8 15 C8 7, 0 5, 0 5 L0 0 L50 0Z" fill="#13c2c2" opacity="0.12" />
      <path d="M15 35 L35 35 M25 25 L25 45" stroke="#13c2c2" strokeWidth="2" strokeLinecap="round" opacity="0.4" />
    </g>
    <g transform="translate(20, 190)">
      <circle cx="20" cy="20" r="18" fill="#eb2f96" opacity="0.1" />
      <path d="M12 20 C12 14, 16 10, 22 10 M28 20 C28 26, 24 30, 18 30" stroke="#eb2f96" strokeWidth="2.5" strokeLinecap="round" opacity="0.35" />
      <circle cx="20" cy="20" r="4" fill="#eb2f96" opacity="0.3" />
    </g>
    <path d="M40 75 Q 50 100, 40 130 Q 30 160, 45 190 Q 60 220, 40 260" stroke="#d9d9d9" strokeWidth="2" strokeDasharray="4 4" fill="none" />
    <g transform="translate(25, 280)">
      <rect x="0" y="0" width="30" height="8" rx="4" fill="#1890ff" opacity="0.15" />
      <rect x="0" y="0" width="20" height="8" rx="4" fill="#1890ff" opacity="0.25" />
    </g>
    <g transform="translate(20, 300)">
      <rect x="0" y="0" width="40" height="8" rx="4" fill="#52c41a" opacity="0.15" />
      <rect x="0" y="0" width="30" height="8" rx="4" fill="#52c41a" opacity="0.25" />
    </g>
    <g transform="translate(25, 320)">
      <rect x="0" y="0" width="30" height="8" rx="4" fill="#722ed1" opacity="0.15" />
      <rect x="0" y="0" width="25" height="8" rx="4" fill="#722ed1" opacity="0.25" />
    </g>
    <g transform="translate(20, 355)">
      <path d="M10 10 L10 0 L30 0 L30 10 C38 10, 38 20, 30 22 L30 28 L25 28 L25 32 L35 35 L35 40 L5 40 L5 35 L15 32 L15 28 L10 28 L10 22 C2 20, 2 10, 10 10Z" fill="#faad14" opacity="0.2" />
    </g>
  </svg>
);

export const AssignedSkillQuizModal = ({ isOpen, onClose, assignments }) => {
  const { t } = useTranslation();
  const [phase, setPhase] = useState('selection');
  const [selectedQuizId, setSelectedQuizId] = useState(null);
  const [selectedTopicId, setSelectedTopicId] = useState(undefined);
  const [questionCount, setQuestionCount] = useState(10);
  const [collectionsById, setCollectionsById] = useState({});
  const [loadingCollectionId, setLoadingCollectionId] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedChoices, setSelectedChoices] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [playSessionToken, setPlaySessionToken] = useState(null);
  const [quizStartedAt, setQuizStartedAt] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const questionDisplayTimeRef = useRef(null);

  const availableAssignments = useMemo(
    () => [...(assignments || [])].sort((left, right) => (left.orderIndex ?? 0) - (right.orderIndex ?? 0)),
    [assignments]
  );

  const selectedCollection = selectedQuizId ? collectionsById[selectedQuizId] || null : null;

  const questionCountOptions = useMemo(
    () => buildQuestionCountOptions(selectedCollection?.questionCount),
    [selectedCollection]
  );

  const currentQuestion = phase === 'quiz' ? questions[currentQuestionIndex] : null;
  const currentAnswer = phase === 'quiz' ? answers[currentQuestionIndex] || null : null;
  const correctCount = answers.filter((answer) => answer.isCorrect).length;
  const totalCount = answers.length;
  const scorePercent = answers.length > 0 ? Math.round((correctCount / answers.length) * 100) : 0;
  const estimateMinutes = Math.max(1, Math.ceil(questionCount * 0.8));

  const difficultyBreakdown = useMemo(() => {
    const breakdown = {};
    answers.forEach((answer) => {
      const level = answer.levelOfDifficultyName || t('quiz.unrated', 'Unrated');
      if (!breakdown[level]) {
        breakdown[level] = {
          correct: 0,
          total: 0,
          color: answer.levelOfDifficultyColor || '#d9d9d9',
        };
      }
      breakdown[level].total += 1;
      if (answer.isCorrect) {
        breakdown[level].correct += 1;
      }
    });
    return breakdown;
  }, [answers, t]);

  const topicProfileData = useMemo(() => {
    const topics = selectedCollection?.topics || [];
    if (topics.length === 0 || answers.length === 0) {
      return null;
    }

    const baseline = {};
    const achieved = {};
    const topicTotals = {};

    topics.forEach((topic) => {
      topicTotals[topic.id] = { possible: 0, achieved: 0, questionCount: 0 };
    });

    answers.forEach((answer) => {
      if (!answer.weights || answer.weights.length === 0) {
        return;
      }

      answer.weights.forEach((weight) => {
        if (topicTotals[weight.topicId] !== undefined) {
          topicTotals[weight.topicId].possible += weight.weight;
          topicTotals[weight.topicId].questionCount += 1;
          if (answer.isCorrect) {
            topicTotals[weight.topicId].achieved += weight.weight;
          }
        }
      });
    });

    let hasData = false;
    topics.forEach((topic) => {
      const total = topicTotals[topic.id];
      if (total.questionCount > 0) {
        hasData = true;
        baseline[topic.id] = Math.round((total.possible / answers.length) * 100);
        achieved[topic.id] = Math.round((total.achieved / answers.length) * 100);
      } else {
        baseline[topic.id] = 0;
        achieved[topic.id] = 0;
      }
    });

    if (!hasData) {
      return null;
    }

    return {
      topics: topics.map((topic) => ({
        id: topic.id,
        name: topic.name,
        color: undefined,
      })),
      baseline,
      achieved,
      topicTotals,
    };
  }, [answers, selectedCollection]);

  const performance = useMemo(() => {
    if (scorePercent >= 90) return { key: 'excellent', label: t('quiz.expert', 'Expert'), emoji: '🏆' };
    if (scorePercent >= 70) return { key: 'good', label: t('quiz.proficient', 'Proficient'), emoji: '⭐' };
    if (scorePercent >= 50) return { key: 'average', label: t('quiz.developing', 'Developing'), emoji: '📈' };
    return { key: 'poor', label: t('quiz.needsPractice', 'Needs Practice'), emoji: '💪' };
  }, [scorePercent, t]);

  const scoreMessage = useMemo(() => {
    if (scorePercent >= 90) return t('quiz.excellent', 'Excellent! Outstanding performance!');
    if (scorePercent >= 70) return t('quiz.good', 'Good job! Well done!');
    if (scorePercent >= 50) return t('quiz.average', 'Not bad! Keep practicing!');
    return t('quiz.needsWork', 'Keep learning! You can do better!');
  }, [scorePercent, t]);

  const loadCollection = useCallback(async (quizCollectionId) => {
    if (!quizCollectionId) {
      return null;
    }

    if (collectionsById[quizCollectionId]) {
      return collectionsById[quizCollectionId];
    }

    setLoadingCollectionId(quizCollectionId);
    try {
      const collection = await quizCollectionsService.getPublicQuizCollection(quizCollectionId, { suppressNotification: true });
      setCollectionsById((prev) => ({ ...prev, [quizCollectionId]: collection }));
      return collection;
    } catch (error) {
      message.error(t('quiz.loadError', 'Failed to load quiz collection'));
      return null;
    } finally {
      setLoadingCollectionId(null);
    }
  }, [collectionsById, t]);

  const resetModalState = useCallback(() => {
    setPhase('selection');
    setSelectedQuizId(null);
    setSelectedTopicId(undefined);
    setQuestionCount(10);
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedChoices([]);
    setShowAnswer(false);
    setAnswers([]);
    setPlaySessionToken(null);
    setQuizStartedAt(null);
    setElapsedTime(0);
    questionDisplayTimeRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetModalState();
      return;
    }

    if (availableAssignments.length > 0 && !selectedQuizId) {
      const firstQuizCollectionId = availableAssignments[0].quizCollectionId;
      setSelectedQuizId(firstQuizCollectionId);
      loadCollection(firstQuizCollectionId).then((collection) => {
        if (collection?.questionCount) {
          setQuestionCount(Math.min(10, collection.questionCount));
        }
      });
    }
  }, [availableAssignments, isOpen, loadCollection, resetModalState, selectedQuizId]);

  useEffect(() => {
    if (phase !== 'quiz' || !quizStartedAt) {
      return undefined;
    }

    const updateElapsedTime = () => {
      setElapsedTime(Math.max(0, Math.floor((Date.now() - quizStartedAt) / 1000)));
    };

    updateElapsedTime();
    const timerId = window.setInterval(updateElapsedTime, 1000);
    return () => window.clearInterval(timerId);
  }, [phase, quizStartedAt]);

  useEffect(() => {
    if (!selectedCollection?.questionCount) {
      return;
    }

    setQuestionCount((currentValue) => {
      if (currentValue > selectedCollection.questionCount) {
        return selectedCollection.questionCount;
      }

      return currentValue;
    });
  }, [selectedCollection]);

  const handleSelectQuiz = useCallback(async (quizCollectionId) => {
    setSelectedQuizId(quizCollectionId);
    setSelectedTopicId(undefined);
    const collection = await loadCollection(quizCollectionId);
    if (collection?.questionCount) {
      setQuestionCount((currentValue) => {
        if (!currentValue || currentValue > collection.questionCount) {
          return Math.min(10, collection.questionCount);
        }
        return currentValue;
      });
    }
  }, [loadCollection]);

  const handleStartQuiz = useCallback(async () => {
    if (!selectedQuizId) {
      message.warning(t('quiz.selectQuizCollection', 'Please select a quiz collection'));
      return;
    }

    setLoadingQuiz(true);
    try {
      const session = await quizCollectionsService.startQuizPlay(
        selectedQuizId,
        {
          questionCount,
          topicId: selectedTopicId || undefined,
          includeAnswerKey: true,
        },
        { suppressNotification: true }
      );

      setQuestions(session.questions || []);
      setCurrentQuestionIndex(0);
      setSelectedChoices([]);
      setShowAnswer(false);
      setAnswers([]);
      setPlaySessionToken(session.playSessionToken || null);
      setQuizStartedAt(session.serverStartedAt ? new Date(session.serverStartedAt).getTime() : Date.now());
      setElapsedTime(0);
      questionDisplayTimeRef.current = Date.now();
      setPhase('quiz');
    } catch (error) {
      message.error(t('quiz.startError', 'Failed to start quiz'));
    } finally {
      setLoadingQuiz(false);
    }
  }, [questionCount, selectedQuizId, selectedTopicId, t]);

  const handleChoiceSelect = useCallback((choiceId) => {
    if (!currentQuestion || showAnswer) {
      return;
    }

    if (currentQuestion.isMultiSelect) {
      setSelectedChoices((prev) => (
        prev.includes(choiceId)
          ? prev.filter((id) => id !== choiceId)
          : [...prev, choiceId]
      ));
      return;
    }

    setSelectedChoices([choiceId]);
  }, [currentQuestion, showAnswer]);

  const handleCheckAnswer = useCallback(() => {
    if (!currentQuestion || selectedChoices.length === 0) {
      message.warning(t('quiz.selectAnswer', 'Please select an answer'));
      return;
    }

    const correctChoiceIds = Array.isArray(currentQuestion.correctChoiceIds)
      ? sortChoiceIds(currentQuestion.correctChoiceIds)
      : [];

    if (correctChoiceIds.length === 0) {
      message.error(t('quiz.localCheckUnavailable', 'This quiz could not be checked locally. Please restart the quiz.'));
      return;
    }

    const normalizedSelectedChoices = sortChoiceIds(selectedChoices);
    const clientDurationMs = questionDisplayTimeRef.current ? Date.now() - questionDisplayTimeRef.current : null;

    setAnswers((prev) => {
      const next = [...prev];
      next[currentQuestionIndex] = {
        questionId: currentQuestion.id,
        questionCode: currentQuestion.code,
        questionBody: currentQuestion.body,
        topic: currentQuestion.topic,
        subtopic: currentQuestion.subtopic,
        levelOfDifficultyName: currentQuestion.levelOfDifficultyName,
        levelOfDifficultyColor: currentQuestion.levelOfDifficultyColor,
        weights: currentQuestion.weights || [],
        choices: currentQuestion.choices || [],
        selectedChoiceIds: normalizedSelectedChoices,
        correctChoiceIds,
        isCorrect: areChoiceSetsEqual(normalizedSelectedChoices, correctChoiceIds),
        isMultiSelect: currentQuestion.isMultiSelect,
        explanation: null,
        clientDurationMs,
      };
      return next;
    });
    setShowAnswer(true);
  }, [currentQuestion, currentQuestionIndex, selectedChoices, t]);

  const persistAttempt = useCallback((completedAnswers, sessionToken, quizCollectionId) => {
    if (!sessionToken || !quizCollectionId) {
      return;
    }

    quizCollectionsService.finishQuiz(
      quizCollectionId,
      {
        playSessionToken: sessionToken,
        answers: completedAnswers.map((answer) => ({
          questionId: answer.questionId,
          selectedChoiceIds: answer.selectedChoiceIds,
          clientDurationMs: answer.clientDurationMs ?? null,
        })),
      },
      { suppressNotification: true }
    ).catch(() => {
      message.warning(t('quiz.saveAnalyticsWarning', 'Your result was shown, but saving quiz analytics failed.'));
    });
  }, [t]);

  const handleNextQuestion = useCallback(() => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setSelectedChoices([]);
      setShowAnswer(false);
      questionDisplayTimeRef.current = Date.now();
      return;
    }

    setPhase('results');
    persistAttempt(answers, playSessionToken, selectedQuizId);
  }, [answers, currentQuestionIndex, persistAttempt, playSessionToken, questions.length, selectedQuizId]);

  const handleRetryQuiz = useCallback(() => {
    setPhase('selection');
    setQuestions([]);
    setCurrentQuestionIndex(0);
    setSelectedChoices([]);
    setShowAnswer(false);
    setAnswers([]);
    setPlaySessionToken(null);
    setQuizStartedAt(null);
    setElapsedTime(0);
    questionDisplayTimeRef.current = null;
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.quizOverlay}>
      <div className={styles.quizModal}>
        <div className={styles.quizHeader}>
          <div className={styles.headerLeft}>
            <MindMapSkillIcon size={28} color="#1890ff" />
            <Title level={4} className={styles.headerTitle}>
              {t('heatPumpSimulation.skillTest', 'Skill Test')}
            </Title>
          </div>
          <Button type="text" icon={<CloseOutlined />} onClick={onClose} className={styles.closeButton} />
        </div>

        <div className={styles.quizContent}>
          {phase === 'selection' && (
            <div className={styles.selectionPhase}>
              <LeftPuzzleDecoration />

              <div className={styles.selectionMainContent}>
                <div className={styles.selectionIntro}>
                  
                  <div className={styles.mapHeaderIcon}>
                    <MindMapSkillIcon size={50} color="#1890ff" />
                  </div>
                  <Title level={4} className={styles.selectionTitle}>
                    {t('heatPumpSimulation.skillQuizSelection.title', 'Choose your assigned skill quiz')}
                  </Title>
                  <Text type="secondary" className={styles.selectionSubtitle}>
                    {t('heatPumpSimulation.skillQuizSelection.subtitle', 'Pick one of the assigned quiz collections, choose how many questions to answer, optionally focus on a topic, and stay inside the simulation the whole time.')}
                  </Text>
                </div>

                <div className={styles.selectionLayout}>
                  <div className={styles.assignmentColumn}>
                    <div className={styles.assignmentColumnHeader}>
                      <span className={styles.sectionEyebrow}>{t('heatPumpSimulation.skillQuizSelection.availableAssignments', 'Available assignments')}</span>
                      <span className={styles.sectionCount}>{availableAssignments.length}</span>
                    </div>
                    {availableAssignments.map((assignment) => {
                      const isSelected = assignment.quizCollectionId === selectedQuizId;

                      return (
                        <button
                          key={assignment.quizCollectionId}
                          type="button"
                          className={`${styles.assignmentCard} ${isSelected ? styles.assignmentCardSelected : ''}`}
                          onClick={() => handleSelectQuiz(assignment.quizCollectionId)}
                        >
                          <div className={styles.assignmentCardTop}>
                            <div>
                              <div className={styles.assignmentName}>
                                {assignment.displayLabel || assignment.quizCollectionName || `${t('trainingProfiles.quizCollection', 'Quiz collection')} ${assignment.quizCollectionId}`}
                              </div>
                              <div className={styles.assignmentSubtitle}>
                                {assignment.quizCollectionName && assignment.displayLabel
                                  ? assignment.quizCollectionName
                                  : t('heatPumpSimulation.skillQuizSelection.assignedQuiz', 'Assigned quiz collection')}
                              </div>
                            </div>
                            {isSelected && <Tag color="blue">{t('common.selected', 'Selected')}</Tag>}
                          </div>
                          <div className={styles.assignmentMeta}>
                            <span>{assignment.publishedQuestionCount || 0} {t('quiz.questions', 'questions')}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <div className={styles.setupColumn}>
                    {selectedQuizId && loadingCollectionId === selectedQuizId && !selectedCollection ? (
                      <div className={styles.loadingBlock}>
                        <Spin />
                      </div>
                    ) : selectedCollection ? (
                      <>
                        <div className={styles.setupHero}>
                          <div className={styles.setupHeroContent}>
                            <span className={styles.sectionEyebrow}>{t('heatPumpSimulation.skillQuizSelection.quizSetup', 'Quiz setup')}</span>
                            <div className={styles.setupName}>{selectedCollection.name}</div>
                            <div className={styles.setupMeta}>
                              <span>{selectedCollection.questionCount || 0} {t('quiz.availableQuestions', 'questions available')}</span>
                              <span>{selectedCollection.topics?.length || 0} {t('trainingProfiles.topics', 'topics')}</span>
                              <span>{estimateMinutes} {t('quiz.minutesEstimate', 'min approx.')}</span>
                            </div>
                          </div>
                        </div>

                        <div className={styles.setupGroup}>
                          <label className={styles.setupLabel}>{t('quiz.numberOfQuestions', 'Number of Questions')}</label>
                          <div className={styles.questionCountPills}>
                            {questionCountOptions.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                className={`${styles.questionCountPill} ${questionCount === option.value ? styles.questionCountPillActive : ''}`}
                                onClick={() => setQuestionCount(option.value)}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {selectedCollection.topics?.length > 0 && (
                          <div className={styles.setupGroup}>
                            <label className={styles.setupLabel}>{t('quiz.filterByTopic', 'Filter by Topic (optional)')}</label>
                            <Select
                              allowClear
                              value={selectedTopicId}
                              onChange={setSelectedTopicId}
                              style={{ width: '100%' }}
                              className={styles.setupSelect}
                              popupClassName={styles.setupSelectPopup}
                              getPopupContainer={(triggerNode) => triggerNode.parentElement}
                              placeholder={t('quiz.allTopics', 'All Topics')}
                              options={selectedCollection.topics.map((topic) => ({
                                value: topic.id,
                                label: topic.name,
                              }))}
                            />
                          </div>
                        )}

                        <div className={styles.selectionActions}>
                          <Button
                            type="primary"
                            icon={<PlayCircleOutlined />}
                            onClick={handleStartQuiz}
                            loading={loadingQuiz}
                            className={styles.startButton}
                          >
                            {t('quiz.startQuiz', 'Start Quiz')}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.emptySetup}>
                        <Text type="secondary">{t('heatPumpSimulation.skillQuizSelection.empty', 'Select a quiz collection to configure the quiz.')}</Text>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <RightPuzzleDecoration />
            </div>
          )}

          {phase === 'quiz' && currentQuestion && (
            <div className={styles.quizPhase}>
              <div className={styles.quizProgressBar}>
                <div className={styles.progressStats}>
                  <div className={styles.progressStat}>
                    <span className={styles.progressLabel}>{t('quiz.question', 'Question')}</span>
                    <span className={styles.progressValue}>{currentQuestionIndex + 1}/{questions.length}</span>
                  </div>
                  <div className={styles.progressStat}>
                    <span className={styles.progressLabel}>{t('quiz.correct', 'Correct')}</span>
                    <span className={styles.progressValueSuccess}>{correctCount}</span>
                  </div>
                </div>
                <div className={styles.timer}><ClockCircleOutlined /> {formatTime(elapsedTime)}</div>
              </div>

              <Progress percent={((currentQuestionIndex + (showAnswer ? 1 : 0)) / questions.length) * 100} showInfo={false} strokeColor="#1890ff" />

              <div className={styles.questionCard}>
                <div className={styles.questionHeader}>
                  <span className={styles.questionCode}>{currentQuestion.code}</span>
                  <Space wrap>
                    {currentQuestion.levelOfDifficultyName && (
                      <Tag color={currentQuestion.levelOfDifficultyColor || 'blue'}>{currentQuestion.levelOfDifficultyName}</Tag>
                    )}
                    {currentQuestion.topic && <Tag>{currentQuestion.topic}</Tag>}
                    {currentQuestion.subtopic && <Tag>{currentQuestion.subtopic}</Tag>}
                  </Space>
                </div>

                <div className={styles.questionBodyLayout}>
                  {currentQuestion.imageUrl && (
                    <div className={styles.questionImageWrap}>
                      <Image src={getFileUrl(currentQuestion.imageUrl)} alt="" className={styles.questionImage} />
                    </div>
                  )}

                  <div className={styles.questionTextWrap}>
                    <div className={styles.questionText} dangerouslySetInnerHTML={{ __html: currentQuestion.body }} />

                    {showAnswer && currentQuestion.pdfUrl && (
                      <a href={getFileUrl(currentQuestion.pdfUrl)} target="_blank" rel="noreferrer" className={styles.referenceLink}>
                        <FilePdfOutlined />
                        <span>{currentQuestion.pdfFileName || t('quiz.reference', 'Reference')}</span>
                      </a>
                    )}
                  </div>
                </div>

                {currentQuestion.isMultiSelect && (
                  <div className={styles.multiSelectHint}>
                    <CheckCircleFilled style={{ color: '#1890ff' }} />
                    <span>{t('quiz.multiSelectHint', 'Select all correct answers')}</span>
                  </div>
                )}

                <div className={styles.choiceList}>
                  {currentQuestion.choices?.map((choice) => {
                    const isSelected = selectedChoices.includes(choice.id);
                    const isCorrectChoice = currentAnswer?.correctChoiceIds?.includes(choice.id);
                    const selectedIncorrectly = showAnswer && isSelected && !isCorrectChoice;

                    let className = styles.choiceCard;
                    if (isSelected && !showAnswer) className += ` ${styles.choiceCardSelected}`;
                    if (showAnswer && isCorrectChoice) className += ` ${styles.choiceCardCorrect}`;
                    if (selectedIncorrectly) className += ` ${styles.choiceCardIncorrect}`;
                    if (showAnswer) className += ` ${styles.choiceCardDisabled}`;

                    return (
                      <div key={choice.id} className={className} onClick={() => handleChoiceSelect(choice.id)}>
                        <div className={styles.choiceMarker}>
                          {showAnswer && isCorrectChoice && <CheckCircleFilled style={{ color: '#52c41a' }} />}
                          {selectedIncorrectly && <CloseCircleFilled style={{ color: '#ff4d4f' }} />}
                          {!showAnswer && isSelected && <CheckCircleFilled style={{ color: '#1890ff' }} />}
                        </div>
                        {choice.imageUrl && <Image src={getFileUrl(choice.imageUrl)} alt="" className={styles.choiceImage} preview={false} />}
                        <span className={styles.choiceText}>{choice.text}</span>
                      </div>
                    );
                  })}
                </div>

                {currentQuestion.law && (
                  <div className={styles.referenceBox}>
                    <div className={styles.referenceHeader}>
                      <FileTextOutlined />
                      <span>{t('quiz.legalReference', 'Legal / Regulatory Reference')}</span>
                    </div>
                    <div className={styles.referenceText}>{currentQuestion.law}</div>
                  </div>
                )}

                {showAnswer && currentAnswer?.explanation && (
                  <div className={styles.explanationBox}>
                    <div className={styles.referenceHeader}>
                      <QuestionCircleOutlined />
                      <span>{t('quiz.explanation', 'Explanation')}</span>
                    </div>
                    <div className={styles.referenceText}>{currentAnswer.explanation}</div>
                  </div>
                )}
              </div>

              <div className={styles.quizActions}>
                <div>
                  {showAnswer && currentAnswer && (
                    <Tag color={currentAnswer.isCorrect ? 'success' : 'error'} icon={currentAnswer.isCorrect ? <CheckCircleFilled /> : <CloseCircleFilled />}>
                      {currentAnswer.isCorrect ? t('quiz.correctAnswer', 'Correct!') : t('quiz.incorrectAnswer', 'Incorrect')}
                    </Tag>
                  )}
                </div>
                <div>
                  {!showAnswer ? (
                    <Button type="primary" onClick={handleCheckAnswer} disabled={selectedChoices.length === 0}>
                      {t('quiz.checkAnswer', 'Check Answer')}
                    </Button>
                  ) : (
                    <Button type="primary" onClick={handleNextQuestion} icon={<RightOutlined />}>
                      {currentQuestionIndex < questions.length - 1 ? t('quiz.nextQuestion', 'Next Question') : t('quiz.viewResults', 'View Results')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {phase === 'results' && (
            <div className={styles.resultsDashboard}>
              <div className={styles.resultsHero}>
                <TrophyOutlined className={styles.resultsHeroIcon} />
                <Title level={3} className={styles.resultsHeroTitle}>
                  {t('quiz.quizComplete', 'Quiz Complete!')}
                </Title>

                <div className={`${styles.scoreCircle} ${styles[performance.key]}`}>
                  <span className={styles.scoreValue}>{scorePercent}%</span>
                  <span className={styles.scoreLabel}>{t('quiz.score', 'Score')}</span>
                </div>

                <div className={`${styles.performanceBadge} ${styles[performance.key]}`}>
                  <span>{performance.emoji}</span>
                  <span>{performance.label}</span>
                </div>

                
                <Text type="secondary" className={styles.scoreMessage}>
                  {scoreMessage}
                </Text>
              </div>

              <div className={styles.statsSummary}>
                <div className={styles.statsSummaryItem}>
                  <span className={styles.statsSummaryValue} style={{ color: '#52c41a' }}>{correctCount}</span>
                  <span className={styles.statsSummaryLabel}>{t('quiz.correct', 'Correct')}</span>
                </div>
                <div className={styles.statDivider} />
                <div className={styles.statsSummaryItem}>
                  <span className={styles.statsSummaryValue} style={{ color: '#ff4d4f' }}>{totalCount - correctCount}</span>
                  <span className={styles.statsSummaryLabel}>{t('quiz.incorrect', 'Incorrect')}</span>
                </div>
                <div className={styles.statDivider} />
                <div className={styles.statsSummaryItem}>
                  <span className={styles.statsSummaryValue}>{formatTime(elapsedTime)}</span>
                  <span className={styles.statsSummaryLabel}>{t('quiz.time', 'Time')}</span>
                </div>
                {Object.keys(difficultyBreakdown).length > 0 && (
                  <>
                    <div className={styles.statDivider} />
                    <div className={styles.statsSummaryItem}>
                      <div className={styles.difficultyBreakdown}>
                        {Object.entries(difficultyBreakdown).map(([level, data]) => (
                          <span key={level} className={styles.difficultyItem}>
                            <span className={styles.difficultyDot} style={{ backgroundColor: data.color }} />
                            <span>{level}</span>
                            <span className={styles.difficultyScore}>{data.correct}/{data.total}</span>
                          </span>
                        ))}
                      </div>
                      <span className={styles.statsSummaryLabel}>{t('quiz.difficulty', 'Difficulty')}</span>
                    </div>
                  </>
                )}
              </div>

              {topicProfileData && topicProfileData.topics.length >= 3 && (
                <div className={styles.radarChartSection}>
                  <RadarChart
                    topics={topicProfileData.topics}
                    baselineData={topicProfileData.baseline}
                    achievedData={topicProfileData.achieved}
                    showLegend={true}
                    showDetails={true}
                    title={`🎯 ${t('quiz.skillProfile', 'Your Skill Profile')}`}
                    baselineLabel={t('quiz.topicCoverage', 'Topic Coverage')}
                    achievedLabel={t('quiz.yourPerformance', 'Your Performance')}
                  />
                </div>
              )}

              {topicProfileData && (
                <div className={styles.relevanceSection}>
                  <div className={styles.relevanceTitle}>{t('quiz.relevanceProfile', 'Quiz Relevance Profile')}</div>
                  <div className={styles.relevanceGrid}>
                    {topicProfileData.topics.map((topic, index) => {
                      const totals = topicProfileData.topicTotals[topic.id];
                      const percent = totals.possible > 0
                        ? Math.round((totals.achieved / totals.possible) * 100)
                        : 0;
                      const color = RADAR_COLORS[index % RADAR_COLORS.length];

                      return (
                        <div key={topic.id} className={styles.relevanceItem}>
                          <div className={styles.relevanceHeader}>
                            <span className={styles.relevanceLabel}>{topic.name}</span>
                            <span className={styles.relevancePercent} style={{ color }}>{percent}%</span>
                          </div>
                          <div className={styles.relevanceBar}>
                            <div className={styles.relevanceFill} style={{ width: `${percent}%`, backgroundColor: color }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className={styles.reviewCard}>
                <div className={styles.reviewTitle}>{t('quiz.questionReview', 'Question Review')}</div>
                <div className={styles.reviewList}>
                  {answers.map((answer, index) => {
                    const selectedTexts = (answer.choices || [])
                      .filter((choice) => answer.selectedChoiceIds.includes(choice.id))
                      .map((choice) => choice.text)
                      .filter(Boolean);

                    const plainBody = answer.questionBody
                      ? answer.questionBody.replace(/<[^>]*>/g, '').slice(0, 120)
                      : '';

                    return (
                      <div
                        key={answer.questionId}
                        className={`${styles.reviewItem} ${answer.isCorrect ? styles.reviewCorrect : styles.reviewIncorrect}`}
                      >
                        <div className={styles.reviewHeader}>
                          <span className={styles.reviewNumber}>
                            Q{index + 1} {answer.questionCode ? `(${answer.questionCode})` : ''}
                          </span>
                          {answer.isCorrect
                            ? <CheckCircleFilled style={{ color: '#52c41a', fontSize: 16 }} />
                            : <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: 16 }} />
                          }
                        </div>
                        <span className={styles.reviewQuestion}>{plainBody}{plainBody.length >= 120 ? '…' : ''}</span>
                        <span className={`${styles.reviewAnswer} ${answer.isCorrect ? styles.reviewAnswerCorrect : styles.reviewAnswerIncorrect}`}>
                          {answer.isCorrect
                            ? <><CheckCircleFilled style={{ marginRight: 4 }} /> {selectedTexts.join(', ') || t('quiz.imageAnswer', '(Image answer)')}</>
                            : <><CloseCircleFilled style={{ marginRight: 4 }} /> {selectedTexts.join(', ') || t('quiz.imageAnswer', '(Image answer)')}</>
                          }
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className={styles.resultsActions}>
                <Button icon={<ReloadOutlined />} onClick={handleRetryQuiz}>{t('quiz.restartQuiz', 'Restart Quiz')}</Button>
                <Button icon={<HomeOutlined />} onClick={() => setPhase('selection')}>{t('quiz.backToSetup', 'Back to setup')}</Button>
                <Button type="primary" onClick={onClose}>{t('common.close', 'Close')}</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

AssignedSkillQuizModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  assignments: PropTypes.arrayOf(PropTypes.shape({
    quizCollectionId: PropTypes.number.isRequired,
    displayLabel: PropTypes.string,
    quizCollectionName: PropTypes.string,
    publishedQuestionCount: PropTypes.number,
    orderIndex: PropTypes.number,
  })),
};

AssignedSkillQuizModal.defaultProps = {
  isOpen: false,
  assignments: [],
};

export default AssignedSkillQuizModal;